<template>
  <general-layout v-if="canPerform('authcore.roles.list')">
    <template v-slot:alert v-if="authenticated">
      <b-alert
        variant="success"
        :show="localShowSuccessAlert"
        dismissible
        @dismissed="localShowSuccessAlert = false"
        class="web-alert-style text-center rounded-0"
      >
        <span class="align-middle">{{ alertText }}</span>
      </b-alert>
      <b-alert
        variant="danger"
        show
        v-if="error !== undefined">
        <i18n path="status.error.general" tag="div">
          <span place="message">{{ error.message }}</span>
        </i18n>
      </b-alert>
      <b-alert
        variant="danger"
        show
        v-if="errorCreate !== undefined">
        <i18n path="status.error.creating" tag="div">
          <span place="message">{{ errorCreate.message }}</span>
        </i18n>
      </b-alert>
      <b-alert
        variant="danger"
        show
        v-if="actionError !== undefined">
        <i18n path="status.error.action" tag="div">
          <span place="message">{{ actionError.message }}</span>
        </i18n>
      </b-alert>
    </template>
    <loading-spinner v-if="!authenticated" />
    <b-container v-else>
      <!-- TODO: Put the loading / error messages into their corresponding places -->
      <b-bsq-modal
        ref="modal"
        v-model="isModalEnabled"
        :header-title="modalHeaderTitle"
        :button-title="modalButtonTitle"
        :button-variant="modalButtonVariant"
        @ok="modalSubmit"
      >
        <div class="mt-3 mb-5">
          <div v-if="isCreateRoleModalEnabled">
            <b-bsq-input
              v-model="createRoleName"
              type="text"
              :label="$t('input.role_name')"
            />
          </div>
          <div v-if="isDeleteRoleModalEnabled">
            <i18n path="role_list.modal.text.delete_role" tag="div">
              <span place="role">{{ selectedRole.name }}</span>
            </i18n>
          </div>
        </div>
      </b-bsq-modal>
      <b-row>
        <b-col>
          <h2>{{ $t('title.roles') }}</h2>
        </b-col>
      </b-row>
      <b-row class="my-3">
        <b-col class="text-right">
          <b-button
            v-if="canPerform('authcore.roles.create')"
            variant="primary"
            size="sm"
            @click="openCreateRoleModal">
            {{ $t('role_list.modal.button.create_role') }}
          </b-button>
        </b-col>
      </b-row>
      <b-table
        :fields="[
                 { key: 'name', label: 'Role name' },
                 { key: 'actions', label: 'Actions' }
                 ]"
        :items="roles"
        class="theme-full item-clickable"
        cellspacing="0"
        @row-clicked="viewItem">
        <template v-slot:cell(name)="data">
          <with-loading-content>
            {{ data.item.name }}
            <b-badge v-if="data.item.isSystemRole">{{ $t('general.system') }}</b-badge>
          </with-loading-content>
        </template>
        <template v-slot:cell(actions)="data">
          <with-loading-content :loading="loading">
            <b-button
              v-if="canPerform('authcore.roles.delete') && !data.item.isSystemRole && !data.item.deleting"
              variant="danger"
              size="sm"
              @click="openDeleteRoleModal(data.item)">
              {{ $t('role_list.modal.button.delete_role') }}
            </b-button>
            <b-button
              v-else-if="canPerform('authcore.roles.delete') && !data.item.isSystemRole && data.item.deleting"
              disabled
              variant="danger"
              size="sm"
              @click="openDeleteRoleModal(data.item)">
              <b-spinner small />
            </b-button>
          </with-loading-content>
        </template>
      </b-table>
    </b-container>
  </general-layout>
  <b-alert v-else variant="danger" show>
    <i18n path="status.error.not_authorized" tag="div">
      <span place="page">{{ $t('general.roles_list') }}</span>
    </i18n>
  </b-alert>
</template>

<script>
import { mapState } from 'vuex'

import router from '@/router'
import store from '@/store'
import i18n from '@/i18n'

import GeneralLayout from '@/components/GeneralLayout.vue'
import WithLoadingContent from '@/components/WithLoadingContent.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

export default {
  name: 'RoleList',
  components: {
    GeneralLayout,
    WithLoadingContent,
    LoadingSpinner
  },

  data () {
    return {
      isCreateRoleModalEnabled: false,
      isDeleteRoleModalEnabled: false,
      localShowSuccessAlert: false,
      alertAction: '',
      isProcessing: false,

      selectedRole: {},
      createRoleName: ''
    }
  },
  computed: {
    ...mapState('client', [
      'authenticated'
    ]),
    ...mapState('management/rolesList', [
      'roles',
      'loading',
      'error',
      'actionError'
    ]),
    ...mapState('management/rolesList/createRoleForm', {
      loadingCreate: 'loading',
      errorCreate: 'error'
    }),
    isModalEnabled: {
      get () {
        return this.isCreateRoleModalEnabled ||
               this.isDeleteRoleModalEnabled
      },
      set () {
        setTimeout(() => {
          this.isCreateRoleModalEnabled = false
          this.isDeleteRoleModalEnabled = false
        })
      }
    },
    modalHeaderTitle () {
      if (this.isCreateRoleModalEnabled) {
        return i18n.t('role_list.modal.title.create_role')
      } else if (this.isDeleteRoleModalEnabled) {
        return i18n.t('role_list.modal.title.delete_role')
      }
      return ''
    },
    modalButtonTitle () {
      if (this.isCreateRoleModalEnabled) {
        return i18n.t('role_list.modal.button.create_role')
      } else if (this.isDeleteRoleModalEnabled) {
        return i18n.t('role_list.modal.button.delete_role')
      }
      return ''
    },
    modalButtonVariant () {
      if (this.isCreateRoleModalEnabled) {
        return 'primary'
      } else if (this.isDeleteRoleModalEnabled) {
        return 'danger'
      }
      return ''
    },
    alertText () {
      if (this.alertAction === 'create') {
        return i18n.t('role_list.text.create_role_successfully')
      } else if (this.alertAction === 'delete') {
        return i18n.t('role_list.text.delete_role_successfully')
      }
      return ''
    }
  },

  watch: {
    async authenticated (newVal) {
      if (newVal) {
        // Clear the error message from unauthenticated state
        store.commit('management/rolesList/CLEAR_STATES')
        await store.dispatch('management/rolesList/list')
      }
    }
  },

  mounted () {
    store.dispatch('management/rolesList/list')
  },
  destroyed () {
    store.commit('management/rolesList/CLEAR_STATES')
    store.commit('management/rolesList/createRoleForm/CLEAR_STATES')
  },

  methods: {
    openCreateRoleModal () {
      this.isCreateRoleModalEnabled = true
      this.createRoleName = ''
    },
    openDeleteRoleModal (role) {
      this.isDeleteRoleModalEnabled = true
      this.selectedRole = role
    },
    async modalSubmit () {
      if (this.isCreateRoleModalEnabled) {
        await this.createRole()
      } else if (this.isDeleteRoleModalEnabled) {
        await this.deleteRole()
      }

      if (this.actionError === undefined) {
        this.$nextTick(() => {
          this.isModalEnabled = false
        })
      }
    },
    async createRole () {
      await store.dispatch('management/rolesList/createRoleForm/create', this.createRoleName)
      this.alertAction = 'create'
      if (this.errorsCreate === undefined) {
        this.localShowSuccessAlert = true
      }
    },
    async deleteRole () {
      await store.dispatch('management/rolesList/deleteRole', this.selectedRole.id)
      this.alertAction = 'delete'
      this.localShowSuccessAlert = true
    },

    viewItem (item) {
      const { id } = item
      router.push({
        name: 'RoleDetails',
        params: { id }
      })
    }
  }
}
</script>
